import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { portalApi, usePortal } from "@/lib/PortalContext";
import { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Lock, Eye, EyeOff, Check } from "lucide-react";

export default function PortalResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { setPortalClient } = usePortal();

  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) toast.error("This reset link is missing a token.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) return;
    if (pw.length < 12) return toast.error("Password must be at least 12 characters");
    if (pw !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      await portalApi.post("/portal/reset-password", { token, password: pw });
      const me = await portalApi.get("/portal/me");
      setPortalClient(me.data);
      toast.success("Password updated — you're signed in.");
      nav("/portal");
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setBusy(false); }
  };

  const rules = [
    { ok: pw.length >= 12, label: "At least 12 characters" },
    { ok: new Set(pw).size >= 3, label: "Not too repetitive" },
    { ok: pw === confirm && pw.length > 0, label: "Both fields match" },
  ];

  return (
    <div className="min-h-screen bg-[color:hsl(var(--background))] flex items-center justify-center px-6 py-14" data-testid="portal-reset">
      <div className="max-w-md w-full">
        <div className="overline text-center">Subtend Studio · Portal</div>
        <h1 className="font-serif font-light text-center mt-4" style={{ fontSize: "40px", lineHeight: 1.05 }}>
          Choose a new password
        </h1>
        {!token ? (
          <div className="luxe-card p-7 mt-10 text-center">
            <p className="text-sm text-muted-foreground">This reset link is invalid. Please request a new one.</p>
            <Link to="/portal/forgot-password" className="btn-outline mt-4 inline-flex">Request a new reset link</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="luxe-card p-7 mt-10 space-y-4" data-testid="reset-form">
            <div>
              <label className="overline text-[10px] block mb-1">New password</label>
              <div className="relative">
                <Lock className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5}/>
                <input
                  data-testid="input-new-pw"
                  type={showPw ? "text" : "password"} autoComplete="new-password"
                  className="luxe-input pl-9 pr-10"
                  value={pw} onChange={(e) => setPw(e.target.value)}
                  minLength={12} required
                />
                <button type="button" tabIndex={-1} onClick={() => setShowPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground" aria-label="Toggle password visibility">
                  {showPw ? <EyeOff className="w-4 h-4" strokeWidth={1.5}/> : <Eye className="w-4 h-4" strokeWidth={1.5}/>}
                </button>
              </div>
            </div>
            <div>
              <label className="overline text-[10px] block mb-1">Confirm new password</label>
              <input
                data-testid="input-confirm-pw"
                type={showPw ? "text" : "password"} autoComplete="new-password"
                className="luxe-input"
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                minLength={12} required
              />
            </div>
            <ul className="text-xs space-y-1" data-testid="pw-rules">
              {rules.map((r) => (
                <li key={r.label} className={`flex items-center gap-2 ${r.ok ? "text-foreground" : "text-muted-foreground"}`}>
                  <Check className={`w-3 h-3 ${r.ok ? "opacity-100" : "opacity-30"}`} strokeWidth={2}/> {r.label}
                </li>
              ))}
            </ul>
            <button type="submit" disabled={busy} data-testid="btn-reset-submit" className="btn-primary w-full justify-center">
              {busy ? "Saving…" : "Set new password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
